import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { MainLayout } from '@/layouts/MainLayout'
import { AuthLayout } from '@/layouts/AuthLayout'
import { RequireAuth } from './guards/RequireAuth'
import { RequireGuest } from './guards/RequireGuest'
import { ResetPasswordGuard } from './guards/ResetPasswordGuard'
import { NotFoundPage } from '@/shared/components/NotFoundPage'
import { RouteErrorBoundary } from '@/shared/components/RouteErrorBoundary'
import { LoginPage } from '@/features/auth/routes/LoginPage'
import { ForgotPasswordPage } from '@/features/auth/routes/ForgotPasswordPage'
import { ResetPasswordPage } from '@/features/auth/routes/ResetPasswordPage'
import { DashboardPage } from '@/features/dashboard/routes/DashboardPage'
import { GetStartedPage } from '@/features/campaigns/routes/GetStartedPage'
import { CampaignListPage } from '@/features/campaigns/routes/CampaignListPage'
import { CampaignDetailPage } from '@/features/campaigns/routes/CampaignDetailPage'
import { CampaignSetupPage } from '@/features/campaigns/routes/CampaignSetupPage'
import { CampaignGoalAnalysisPage } from '@/features/campaigns/routes/CampaignGoalAnalysisPage'
import { CampaignLinesOfInquiryPage } from '@/features/campaigns/routes/CampaignLinesOfInquiryPage'
import { CampaignTeamLaunchPage } from '@/features/campaigns/routes/CampaignTeamLaunchPage'
import { TenantListPage } from '@/features/tenants/routes/TenantListPage'
import { SignalProviderListPage } from '@/features/signal-providers/routes/SignalProviderListPage'
import { SensemakerOverviewPage } from '@/features/tenants/routes/SensemakerOverviewPage'
import {
  NotificationManagementStubPage,
} from '@/features/platform/routes/platformAdminStubs'
import { UserListPage } from '@/features/users/routes/UserListPage'
import { TeamInviteAcceptPage } from '@/features/users/routes/TeamInviteAcceptPage'
import { InstrumentListPage } from '@/features/instruments/routes/InstrumentListPage'
import { LibraryLandingPage } from '@/features/library/routes/LibraryLandingPage'
import { UnauthorizedPage } from '@/features/auth/routes/UnauthorizedPage'
import { InstrumentDashboardPageSkeleton } from '@/features/campaigns/components/InstrumentDashboardPageSkeleton'
import { QuestionDrillDownPageSkeleton } from '@/features/campaigns/components/QuestionDrillDownPageSkeleton'
import {
  RedirectLegacyInstrumentDashboard,
  RedirectLegacyLoiDashboard,
  RedirectLegacyQuestionDrillDown,
} from '@/features/campaigns/routes/dashboardRouteRedirects'
import { InstrumentTemplateLibrarySkeleton } from '@/features/instruments/templates/components/skeletons'
import { CompaniesLandingSkeleton } from '@/features/companies/components/skeletons/CompaniesSkeletons'

const CompaniesLandingPage = lazy(async () => {
  const m = await import('@/features/companies/routes/CompaniesLandingPage')
  return { default: m.CompaniesLandingPage }
})
const CompanyDetailPage = lazy(async () => {
  const m = await import('@/features/companies/routes/CompanyDetailPage')
  return { default: m.CompanyDetailPage }
})
const CompanyEditPage = lazy(async () => {
  const m = await import('@/features/companies/routes/CompanyEditPage')
  return { default: m.CompanyEditPage }
})
const CompanyNewPage = lazy(async () => {
  const m = await import('@/features/companies/routes/CompanyNewPage')
  return { default: m.CompanyNewPage }
})

const ReportsLandingPage = lazy(async () => {
  const m = await import('@/features/reports/routes/ReportsLandingPage')
  return { default: m.ReportsLandingPage }
})

const InstrumentReportPage = lazy(async () => {
  const m = await import('@/features/reports/routes/InstrumentReportPage')
  return { default: m.InstrumentReportPage }
})

const LoiReportPage = lazy(async () => {
  const m = await import('@/features/reports/routes/LoiReportPage')
  return { default: m.LoiReportPage }
})

const CampaignReportPage = lazy(async () => {
  const m = await import('@/features/reports/routes/CampaignReportPage')
  return { default: m.CampaignReportPage }
})

const AccountSettingsPage = lazy(async () => {
  const m = await import('@/features/settings/routes/AccountSettingsPage')
  return { default: m.AccountSettingsPage }
})

const ChangeEmailPage = lazy(async () => {
  const m = await import('@/features/settings/routes/ChangeEmailPage')
  return { default: m.ChangeEmailPage }
})

const CreateInstrumentPage = lazy(async () => {
  const m = await import('@/features/instruments/routes/CreateInstrumentPage')
  return { default: m.CreateInstrumentPage }
})

const InstrumentInfoPage = lazy(async () => {
  const m = await import('@/features/instruments/routes/InstrumentInfoPage')
  return { default: m.InstrumentInfoPage }
})

const TemplateLibraryPage = lazy(async () => {
  const m = await import('@/features/instruments/templates/routes/TemplateLibraryPage')
  return { default: m.TemplateLibraryPage }
})

const PlatformTemplateLibraryPage = lazy(async () => {
  const m = await import('@/features/platform/routes/PlatformTemplateLibraryPage')
  return { default: m.PlatformTemplateLibraryPage }
})

const QuestionBuilderWorkspacePage = lazy(async () => {
  const m = await import(
    '@/features/instruments/routes/QuestionBuilderWorkspacePage'
  )
  return { default: m.QuestionBuilderWorkspacePage }
})

const LoDashboardPage = lazy(async () => {
  const m = await import('@/features/campaigns/routes/LoDashboardPage')
  return { default: m.LoDashboardPage }
})

const InstrumentDashboardPage = lazy(async () => {
  const m = await import('@/features/campaigns/routes/InstrumentDashboardPage')
  return { default: m.InstrumentDashboardPage }
})

const QuestionDrillDownPage = lazy(async () => {
  const m = await import('@/features/campaigns/routes/QuestionDrillDownPage')
  return { default: m.QuestionDrillDownPage }
})

const InstrumentRoutingPage = lazy(async () => {
  const m = await import('@/features/instruments/routes/InstrumentRoutingPage')
  return { default: m.InstrumentRoutingPage }
})

const TargetAudiencePage = lazy(async () => {
  const m = await import('@/features/instruments/publish/target-audience/routes/TargetAudiencePage')
  return { default: m.TargetAudiencePage }
})

const DistributionChannelsPage = lazy(async () => {
  const m = await import('@/features/instruments/publish/distribution-channels/routes/DistributionChannelsPage')
  return { default: m.DistributionChannelsPage }
})

const InstrumentTypePage = lazy(async () => {
  const m = await import('@/features/instruments/publish/instrument-type/routes/InstrumentTypePage')
  return { default: m.InstrumentTypePage }
})

const CampaignBudgetPage = lazy(async () => {
  const m = await import('@/features/instruments/publish/campaign-budget/routes/CampaignBudgetPage')
  return { default: m.CampaignBudgetPage }
})

const ReviewPublishPage = lazy(async () => {
  const m = await import('@/features/instruments/publish/review-publish/routes/ReviewPublishPage')
  return { default: m.ReviewPublishPage }
})

function InstrumentRouteFallback() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 8,
      }}
    >
      <CircularProgress aria-label="Loading" />
    </Box>
  )
}

/** Shared route tree for `createBrowserRouter` (app) and `createMemoryRouter` (tests). */
export const appRouteObjects: RouteObject[] = [
  {
    element: <AuthLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <RequireGuest />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { path: '/login', element: <LoginPage />, errorElement: <RouteErrorBoundary /> },
          {
            path: '/forgot-password',
            element: <ForgotPasswordPage />,
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },
      {
        element: <ResetPasswordGuard />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: '/reset-password',
            element: <ResetPasswordPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: '/reset-password/:token',
            element: <ResetPasswordPage />,
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },
      /** Public team invite (email link) — not behind RequireGuest so admins can open links in same browser. */
      {
        path: '/team/invite/:token',
        element: <TeamInviteAcceptPage />,
        errorElement: <RouteErrorBoundary />,
      },
    ],
  },
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <MainLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { index: true, element: <DashboardPage />, errorElement: <RouteErrorBoundary /> },
          {
            path: 'dashboard',
            element: <Navigate to="/" replace />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'campaigns/new',
            element: <Navigate to="/campaigns/new/setup" replace />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'campaigns/:campaignId/setup',
            element: <CampaignSetupPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'campaigns/:campaignId/goal-analysis',
            element: <CampaignGoalAnalysisPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'campaigns/:campaignId/lines-of-inquiry',
            element: <CampaignLinesOfInquiryPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'campaigns/:campaignId/team-and-launch',
            element: <CampaignTeamLaunchPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'response-dashboard/:loiId',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <LoDashboardPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'loi-dashboard/:loiId',
            element: <RedirectLegacyLoiDashboard />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'instrument-dashboard/:instrumentId',
            element: (
              <Suspense
                fallback={
                  <Box sx={{ bgcolor: '#F9FAFB', p: '24px 32px' }}>
                    <InstrumentDashboardPageSkeleton />
                  </Box>
                }
              >
                <InstrumentDashboardPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'question-dashboard/:instrumentId/:questionId',
            element: (
              <Suspense
                fallback={
                  <Box sx={{ bgcolor: '#F9FAFB', p: '24px 32px' }}>
                    <QuestionDrillDownPageSkeleton />
                  </Box>
                }
              >
                <QuestionDrillDownPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'campaigns/:campaignId/loi/:loiId/response-dashboard',
            element: <RedirectLegacyLoiDashboard />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'campaigns/:campaignId/loi/:loiId/instruments/:instrumentId/response-dashboard',
            element: <RedirectLegacyInstrumentDashboard />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'campaigns/:campaignId/loi/:loiId/instruments/:instrumentId/questions/:questionId/response-dashboard',
            element: <RedirectLegacyQuestionDrillDown />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'campaigns/get-started',
            element: <GetStartedPage />,
            errorElement: <RouteErrorBoundary />,
          },
          { path: 'campaigns', element: <CampaignListPage />, errorElement: <RouteErrorBoundary /> },
          { path: 'campaigns/:campaignId', element: <CampaignDetailPage />, errorElement: <RouteErrorBoundary /> },
          {
            path: 'settings',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <AccountSettingsPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'settings/change-email',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <ChangeEmailPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },
    ],
  },
  {
    element: <RequireAuth allowedRoles={['ADMIN', 'EDITOR', 'VIEWER']} />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <MainLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: 'reports',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <ReportsLandingPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'reports/instrument',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <InstrumentReportPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'reports/loi',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <LoiReportPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'reports/campaign',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <CampaignReportPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },
    ],
  },
  {
    element: <RequireAuth allowedRoles={['ADMIN', 'EDITOR', 'VIEWER', 'PLATFORM_ADMIN']} />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <MainLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { path: 'library', element: <LibraryLandingPage />, errorElement: <RouteErrorBoundary /> },
        ],
      },
    ],
  },
  {
    element: <RequireAuth allowedRoles={['ADMIN', 'EDITOR', 'PLATFORM_ADMIN']} />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <MainLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { path: 'instruments', element: <InstrumentListPage />, errorElement: <RouteErrorBoundary /> },
          {
            path: 'instruments/create',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <CreateInstrumentPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'instruments/create/templates',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <TemplateLibraryPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'instruments/create/info',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <InstrumentInfoPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'instruments/:instrumentId/info',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <InstrumentInfoPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'instruments/:instrumentId/builder',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <QuestionBuilderWorkspacePage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'instruments/:instrumentId/routing',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <InstrumentRoutingPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'instruments/:instrumentId/publish/target-audience',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <TargetAudiencePage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'instruments/:instrumentId/publish/distribution-channels',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <DistributionChannelsPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'instruments/:instrumentId/publish/instrument-type',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <InstrumentTypePage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'instruments/:instrumentId/publish/campaign-budget',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <CampaignBudgetPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'instruments/:instrumentId/publish/review-publish',
            element: (
              <Suspense fallback={<InstrumentRouteFallback />}>
                <ReviewPublishPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },
    ],
  },
  /** Companies — Admin / Editor only (BFP-1008). Viewer: nav hidden + 403. */
  {
    element: <RequireAuth allowedRoles={['ADMIN', 'EDITOR']} />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <MainLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: 'companies',
            element: (
              <Suspense fallback={<CompaniesLandingSkeleton />}>
                <CompaniesLandingPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'companies/new',
            element: (
              <Suspense fallback={<CompaniesLandingSkeleton />}>
                <CompanyNewPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'companies/:companyId',
            element: (
              <Suspense fallback={<CompaniesLandingSkeleton />}>
                <CompanyDetailPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'companies/:companyId/edit',
            element: (
              <Suspense fallback={<CompaniesLandingSkeleton />}>
                <CompanyEditPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },
    ],
  },
  /** Sensemaker tenant admin only — team APIs require SensemakerAdmin (platform). */
  {
    element: <RequireAuth allowedRoles={['ADMIN']} />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <MainLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { path: 'users', element: <UserListPage />, errorElement: <RouteErrorBoundary /> },
        ],
      },
    ],
  },
  {
    element: (
      <RequireAuth allowedRoles={['PLATFORM_ADMIN']} />
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <MainLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { path: 'tenants', element: <TenantListPage />, errorElement: <RouteErrorBoundary /> },
          {
            path: 'tenants/:tenantId',
            element: <SensemakerOverviewPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'signal-providers',
            element: <SignalProviderListPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'notification-management',
            element: <NotificationManagementStubPage />,
            errorElement: <RouteErrorBoundary />,
          },
          {
            path: 'platform/template-library',
            element: (
              <Suspense fallback={<InstrumentTemplateLibrarySkeleton />}>
                <PlatformTemplateLibraryPage />
              </Suspense>
            ),
            errorElement: <RouteErrorBoundary />,
          },
        ],
      },
    ],
  },
  {
    path: '/unauthorized',
    element: <AuthLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <UnauthorizedPage />, errorElement: <RouteErrorBoundary /> },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage homeTo="/" />,
    errorElement: <RouteErrorBoundary />,
  },
]

export const router = createBrowserRouter(appRouteObjects)
